/* eslint-disable jsdoc/require-param */
/* eslint-disable no-console */
/**
 * Flow
 *
 *
 * Login / Register
 *
 * -> linking waiting room
 *
 * -> setup skin
 *
 * -> choose treatment
 *
 * -> have you had a consult / disclaimer
 *
 * -> choose timeslots
 *
 * -> confirm
 *
 * -> confirmation page
 *
 */

import wretch from 'wretch';

const dataLayer = (window.dataLayer = window.dataLayer || []);

/**
 * Hardcoded product ids
 */
const CONSULT_EM_SCULPT = -9999;
const CONSULT_COOL_SCULPT = -9998;
const CONSULT_LASER_HAIR_REMOVAL = -9997;
const CONSULT_LASER_SKIN_TREATMENTS = -9996;

/**
 * User statuses
 */
const STATUS_NOT_LOGGED_IN = 'not_logged_in';
const STATUS_NEEDS_FORM = 'needs_form';
const STATUS_PENDING_MATCH = 'pending_match';
const STATUS_FAILED_MATCH = 'failed_match';
const STATUS_NEEDS_VALIDATION = 'needs_validation';
const STATUS_READY = 'ready';

/**
 * WP AJAX URL
 */
let ajaxUrl = document.body?.dataset?.ajaxurl ?? '';

if (typeof silkApiWp !== 'undefined') {
	ajaxUrl = silkApiWp.ajaxUrl;
}

const SELECTOR_ROOT_ELEMENT = '.wp-block-silk-api-make-booking';

let rootElement: HTMLElement;

/**
 * Cookie helpers
 */
function setCookie(cookieName: string, cookieValue: any, expirationDays: number) {
	const d = new Date();
	d.setTime(d.getTime() + expirationDays * 24 * 60 * 60 * 1000);
	const expires = 'expires=' + d.toUTCString();
	document.cookie = cookieName + '=' + cookieValue + ';' + expires + ';path=/';
}

/**
 * Cookie helpers
 */
function getCookie(cookieName: string) {
	const name = cookieName + '=';
	const ca = document.cookie.split(';');
	for (let i = 0; i < ca.length; i++) {
		let c = ca[i];
		while (c.charAt(0) === ' ') {
			c = c.substring(1);
		}
		if (c.indexOf(name) === 0) {
			return c.substring(name.length, c.length);
		}
	}
	return '';
}

/**
 * On page load, try to get the root element
 */
window.addEventListener('load', () => {
	const element = document.querySelector(SELECTOR_ROOT_ELEMENT) as HTMLElement;

	if (element === null) {
		return;
	}

	rootElement = element;

	// TODO move this into the block
	rootElement.style.maxWidth = 'none';
	rootElement.style.display = 'flex';
	rootElement.style.flexDirection = 'column';
	rootElement.style.alignItems = 'stretch';

	initializeRootElement();
});

/**
 * Initialise the root element
 */
async function initializeRootElement() {
	checkIfLoggedInAndProceed();
}

const PAGE_LOADING = 'PAGE_LOADING';
const PAGE_LOGIN_REGISTER = 'PAGE_LOGIN_REGISTER';
const PAGE_USER_INFO = 'PAGE_USER_INFO';
const PAGE_PENDING_MATCH = 'PAGE_PENDING_MATCH';
const PAGE_FAILED_MATCH = 'PAGE_FAILED_MATCH';
const PAGE_NEEDS_VALIDATION = 'PAGE_NEEDS_VALIDATION';
const PAGE_CHOOSE_TREATMENT = 'PAGE_CHOOSE_TREATMENT';
const PAGE_CHOOSE_BOOKING = 'PAGE_CHOOSE_BOOKING';
const PAGE_BOOKING_CONFIRMED = 'PAGE_BOOKING_CONFIRMED';

let currentPage: string = PAGE_LOADING;

/**
 * Check if the user is logged in and proceed to choose treatment
 */
async function checkIfLoggedInAndProceed() {
	// Hide location selector
	const siteClinicLocationSelector = document.querySelector('.clinic-location') as HTMLElement;
	if (siteClinicLocationSelector !== null) {
		siteClinicLocationSelector.style.display = 'none';
	}

	const userStatus = await getUserStatus();

	// const STATUS_ERROR = 'error';

	switch (userStatus) {
		case STATUS_NOT_LOGGED_IN:
			return loadLoginRegisterPage();
		case STATUS_NEEDS_FORM:
			return loadUserInfoPage();
		case STATUS_PENDING_MATCH:
			return loadAccountLinkPage();
		case STATUS_FAILED_MATCH:
			return loadFailedLinkPage();
		case STATUS_NEEDS_VALIDATION:
			return loadAccountVerificationPage();
		case STATUS_READY:
			return loadChooseTreatmentPage();
	}

	// STATUS_ERROR / default
	return loadErrorPage();
}

/**
 * Generic error page
 */
function loadErrorPage() {
	rootElement.innerHTML = '<div class="w-100 p-3 my-5 text-center fs-4">An error has occurred. Please try again later.</div>';
}

// /**
//  * Call the WordPress AJAX endpoint for SILK
//  *
//  * @param  method
//  * @param  action
//  * @param  body
//  * @return any
//  */
// async function callWpOld(method: string, action: string, body: any = {}) {
// 	action = 'silk_api_' + action;

// 	let url = ajaxUrl;
// 	const payload = {
// 		method,
// 		body: undefined,
// 		headers: undefined,
// 	};

// 	// Put the action into the form
// 	if (body instanceof FormData) {
// 		body.append('action', action);
// 	} else {
// 		body = {
// 			...body,
// 			action,
// 		};
// 	}

// 	// Get requests don't have a body
// 	if (method === 'GET') {
// 		let parameters;
// 		if (body instanceof FormData) {
// 			parameters = [...body.entries()].map((e) => encodeParam(e));
// 		} else {
// 			parameters = Object.entries(body).map((e) => encodeParam(e));
// 		}
// 		url += '?' + parameters.join('&');
// 	} else {
// 		url += '?action=' + action;
// 		payload.body = JSON.stringify(body);
// 		payload.headers = {
// 			'Content-Type': body instanceof FormData ? 'application/x-www-form-urlencoded' : 'application/json',
// 		};
// 	}

// 	const response = await fetch(url, payload);
// 	const result = await response.json();
// 	return result;
// }

async function callWp(method: string, action: string, data: any = {}) {
	action = 'silk_api_' + action;

	// Put the action into the form
	if (data instanceof FormData) {
		data.append('action', action);
	} else {
		data.action = action;
	}

	// Encode query params...?
	let url = ajaxUrl;

	try {
		if (method === 'GET') {
			let parameters;
			if (data instanceof FormData) {
				parameters = [...data.entries()].map((e) => encodeParam(e));
			} else {
				parameters = Object.entries(data).map((e) => encodeParam(e));
			}
			url += '?' + parameters.join('&');

			return await wretch(url).get().json();
		}

		if (method === 'POST') {
			if (data instanceof FormData) {
				return await wretch(url).body(data).post().json();
			}
			return await wretch(url).formData(data, true).post().json();
		}
	} catch (e) {
		console.error(`[Booking] There was an error with the request`, e);
		return null;
	}

	console.error(`[Booking] Unimplemented HTTP Request Method ${method}`);
	return null;
}

/**
 * Custom get encoding of requests to pass to the backend
 *
 * @param  e
 */
function encodeParam(e: [string, unknown]): string {
	if (e[1] === null || e[1] === undefined) {
		return '';
	}

	if (e[1] instanceof Boolean || typeof e[1] === 'boolean') {
		return encodeURIComponent(e[0]) + '=' + encodeURIComponent(e[1] ? '1' : '0');
	}

	if (e[1] instanceof Array) {
		const params: string[] = [];

		e[1].forEach((value) => params.push(encodeURIComponent(e[0]) + '[]=' + encodeURIComponent(value as any)));

		return params.join('&');
	}

	return encodeURIComponent(e[0]) + '=' + encodeURIComponent(e[1] as any);
}

/**
 * Spinner
 */
function showSpinner() {
	window.scroll({ top: 0 });
	rootElement.innerHTML = getSpinnerHtmlFull();
}

/**
 * Spinner HTML
 */
function getSpinnerHtmlFull() {
	return `<div class="d-flex align-items-center justify-content-center my-5 py-5" style="min-height: 10rem"><div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div></div>`;
}
/**
 * Spinner HTML
 */
function getSpinnerHTML() {
	return `<div class="d-flex align-items-center justify-content-center my-5 py-5"><div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div></div>`;
}

/**
 * Login
 */
/**
 * Submit the login form
 *
 * @param  action
 * @param  form
 */
async function submitLoginForm(action: string, form: HTMLFormElement) {
	let errorElement = form.querySelector('.error') as HTMLElement;

	const submitButtons = form.querySelectorAll('button');

	submitButtons.forEach((button) => {
		(button.querySelector('.text') as HTMLSpanElement).style.display = 'none';
		(button.querySelector('.spinner') as HTMLSpanElement).style.display = 'block';
	});

	// Create an error element
	if (errorElement === null) {
		errorElement = document.createElement('div');
		errorElement.classList.add('error');
		errorElement.classList.add('alert');
		errorElement.classList.add('alert-danger');
		form.prepend(errorElement);
	}

	errorElement.innerHTML = '';
	errorElement.style.display = 'none';

	// Get the form
	const formData = new FormData(form);

	// Try to login
	const result = await callWp('POST', action, formData);

	// Success
	const success = result.success ?? false;

	// Check for error
	if (!success) {
		const error = result.error ?? 'Unknown error';

		errorElement.innerHTML = error;
		errorElement.style.display = 'block';

		submitButtons.forEach((button) => {
			(button.querySelector('.text') as HTMLSpanElement).style.display = 'block';
			(button.querySelector('.spinner') as HTMLSpanElement).style.display = 'none';
		});

		return;
	}

	checkIfLoggedInAndProceed();
}

/**
 * Load the login / register page
 */
async function loadLoginRegisterPage() {
	dataLayer.push({
		bookingLocation: selectedBookingLocation ?? 'Unknown',
		event: 'pageview',
		page_location: '/booking-online/redirect-to-login/',
		page_title: 'Booking online - 1 - Redirect to login',
	});
	// We will redirect to woocommerce login, then redirect back here
	window.location.href = (document.body.dataset.directoryUrl ?? '/') + 'silk/my-account/?redirect_to_booking=1';
	return;

	currentPage = PAGE_LOGIN_REGISTER;

	const page = await callWp('GET', 'view_login_register');

	rootElement.innerHTML = page.html;

	/**
	 * Attach submit listener to the login form
	 */
	const loginForm = document.getElementById('loginForm') as HTMLFormElement;
	if (loginForm !== null) {
		loginForm.addEventListener('submit', (e) => {
			e.preventDefault();
			submitLoginForm('login', loginForm);
		});
	}

	/**
	 * Attach submit listener to the register form
	 */
	const registerForm = document.getElementById('registerForm') as HTMLFormElement;
	if (registerForm !== null) {
		registerForm.addEventListener('submit', (e) => {
			e.preventDefault();
			submitLoginForm('register', registerForm);
		});
	}
}

/**
 * Check if the current user is logged in
 */
async function getUserStatus() {
	const reponse = await callWp('GET', 'get_user_status');

	console.log(reponse);

	return reponse.status ?? 'error';
}

/**
 * User Info
 */
const USER_INFO_FORM_ID = 'userInfoForm';
const USER_INFO_FORM_SUBMIT_BUTTON_ID = 'userInfoFormSubmit';

/**
 * Load the user info page
 */
async function loadUserInfoPage() {
	dataLayer.push({
		bookingLocation: selectedBookingLocation ?? 'Unknown',
		event: 'pageview',
		page_location: '/booking-online/booking-prerequisites/',
		page_title: 'Booking online - 2 - Booking Prerequisites',
	});

	currentPage = PAGE_USER_INFO;

	showSpinner();

	const page = await callWp('GET', 'view_user_info');

	rootElement.innerHTML = page.html;

	rebindAllLocationSelectors();

	const userInfoForm = document.getElementById(USER_INFO_FORM_ID) as HTMLFormElement;
	if (userInfoForm !== null) {
		userInfoForm.addEventListener('submit', (e) => {
			e.preventDefault();
			submitUserInfoForm(userInfoForm);
		});
	}
}

/**
 * Submit the user info form
 *
 * @param  userInfoForm
 */
async function submitUserInfoForm(userInfoForm: HTMLFormElement) {
	let errorElement = userInfoForm.querySelector('.error') as HTMLElement;

	const userInfoFormSubmitButton = document.getElementById(USER_INFO_FORM_SUBMIT_BUTTON_ID) as HTMLButtonElement;
	const text = userInfoFormSubmitButton.querySelector('.text') as HTMLSpanElement;
	const spinner = userInfoFormSubmitButton.querySelector('.spinner') as HTMLSpanElement;

	if (userInfoFormSubmitButton === null || text === null || spinner === null) {
		return;
	}

	userInfoFormSubmitButton.disabled = true;
	text.style.display = 'none';
	spinner.style.display = 'block';

	// Create an error element
	if (errorElement === null) {
		errorElement = document.createElement('div');
		errorElement.classList.add('error');
		errorElement.classList.add('alert');
		errorElement.classList.add('alert-danger');
		userInfoForm.prepend(errorElement);
	}

	errorElement.innerHTML = '';
	errorElement.style.display = 'none';

	// Get the form
	const formData = new FormData(userInfoForm);

	// Add the location id
	formData.append('location', simpleSalonCompanyId ?? '');

	// Try to submit the user info
	const result = await callWp('POST', 'submit_user_info', formData);

	// Success
	const success = result.success ?? false;

	// Check for error
	if (!success) {
		const errors = result.errors ?? [];

		let errorHTML = '';

		if (errors.length === 0) {
			errorHTML = 'Unknown error';
		}

		for (const [key, error] of Object.entries(errors)) {
			errorHTML += `<li>${error}</li>`;
		}

		errorElement.innerHTML = `<ul>${errorHTML}</ul>`;
		errorElement.style.display = 'block';

		userInfoFormSubmitButton.disabled = false;
		text.style.display = 'block';
		spinner.style.display = 'none';

		const scrollToOffset = -130;
		const scrollToY = errorElement.getBoundingClientRect().top + window.pageYOffset + scrollToOffset;

		window.scrollTo({ top: scrollToY, behavior: 'smooth' });

		return;
	}

	checkIfLoggedInAndProceed();
}

/**
 * Load the user account link page / waiting room
 */
async function loadAccountLinkPage() {
	dataLayer.push({
		bookingLocation: selectedBookingLocation ?? 'Unknown',
		event: 'pageview',
		page_location: '/booking-online/pending-account-match/',
		page_title: 'Booking online - 3.2 - Pending account match',
	});

	currentPage = PAGE_PENDING_MATCH;
	showSpinner();

	const page = await callWp('GET', 'view_account_link');

	rootElement.innerHTML = page.html;

	checkForAccountLink();
}

/**
 * Load the failed link page
 */
async function loadFailedLinkPage() {
	dataLayer.push({
		bookingLocation: selectedBookingLocation ?? 'Unknown',
		event: 'pageview',
		page_location: '/booking-online/failed-account-match/',
		page_title: 'Booking online - 3.3 - Failed account match',
	});

	currentPage = PAGE_FAILED_MATCH;
	showSpinner();

	const page = await callWp('GET', 'view_account_link_failed');

	rootElement.innerHTML = page.html;
}

async function checkForAccountLink() {
	const userStatus = await getUserStatus();

	if (![STATUS_FAILED_MATCH, STATUS_NEEDS_VALIDATION, STATUS_READY].includes(userStatus)) {
		setTimeout(checkForAccountLink, 15000);
		return;
	}

	// Verify the account link (may require sms or email?)
	if (userStatus === STATUS_FAILED_MATCH) {
		return loadFailedLinkPage();
	}

	if (userStatus === STATUS_NEEDS_VALIDATION) {
		return loadAccountVerificationPage();
	}

	// Proceed to choose treatment
	loadChooseTreatmentPage();
}

/**
 * Load the user account link page / waiting room
 */
async function loadAccountVerificationPage() {
	dataLayer.push({
		bookingLocation: selectedBookingLocation ?? 'Unknown',
		event: 'pageview',
		page_location: '/booking-online/ask-to-verify/',
		page_title: 'Booking online - 3.1 - Ask to verify',
	});

	currentPage = PAGE_NEEDS_VALIDATION;

	const page = await callWp('GET', 'view_account_verification');

	rootElement.innerHTML = page.html;

	// Bind resend button
	(document.getElementById('accountVerificationResendCodeButton') as HTMLButtonElement)?.addEventListener('click', async function (e) {
		console.log('resend');
		this.disabled = true;
		const response = await callWp('POST', 'send_verification_code');
		console.log(response);
		this.disabled = false;
	});

	// Send verification form
	(document.getElementById('accountVerificationForm') as HTMLFormElement)?.addEventListener('submit', async function (e) {
		e.preventDefault();

		const errorElement = document.getElementById('accountVerificationError') as HTMLDivElement;

		const submitButton = document.getElementById('accountVerificationSubmitCodeButton') as HTMLButtonElement;
		const resendButton = document.getElementById('accountVerificationResendCodeButton') as HTMLButtonElement;

		const loadingSpinner = document.getElementById('accountVerificationLoadingSpinner') as HTMLDivElement;

		const verificationCode = (document.getElementById('accountVerificationSubmitCode') as HTMLInputElement)?.value ?? '';

		errorElement.style.display = 'none';
		submitButton.style.display = 'none';
		resendButton.style.display = 'none';

		loadingSpinner.style.display = 'flex';

		const response = await callWp('POST', 'submit_verification_code', { verificationCode });

		loadingSpinner.style.display = 'none';

		submitButton.style.display = 'block';
		resendButton.style.display = 'block';

		if (response.success) {
			checkIfLoggedInAndProceed();
		} else {
			errorElement.style.display = 'block';
		}
	});

	// Bind the send button
	// accountVerificationSubmitCode
	// accountVerificationSubmitCodeButton
	// accountVerificationResendCodeButton

	// Bind the re-submit button

	// Set a timer to check if the account is linked every 15 seconds
	// checkForAccountLink();
}

/**
 * Choose treatment
 */
let selectedCategorySlug = '';
let productIds: number[] = [];
let consultChecked = false;
let includeConsult = false;
/**
 * Load the choose treatment page
 */
async function loadChooseTreatmentPage() {
	dataLayer.push({
		bookingLocation: selectedBookingLocation ?? 'Unknown',
		event: 'pageview',
		page_location: '/booking-online/choose-treatment',
		page_title: 'Booking online - 4 - Choose Treatment',
	});

	currentPage = PAGE_CHOOSE_TREATMENT;
	showSpinner();

	const response = await callWp('GET', 'view_choose_treatment');

	rootElement.innerHTML = response.html;

	if (selectedCategorySlug !== '') {
		const triggerEl = document.querySelector(`#chooseTreatmentTabs .treatment-category[data-category-slug="${selectedCategorySlug}"]`);
		(Tab.getInstance(triggerEl) ?? new Tab(triggerEl))?.show();
	} else {
		selectedCategorySlug = (document.querySelector('#chooseTreatmentTabs .treatment-category.active') as HTMLElement)?.dataset.categorySlug ?? '';
	}

	updateAddonsModalCategories();

	// Modals
	const consultModalLaser = new Modal(document.getElementById('consultModalLaser'), {
		keyboard: false,
		backdrop: 'static',
	});
	const consultModalSkin = new Modal(document.getElementById('consultModalSkin'), {
		keyboard: false,
		backdrop: 'static',
	});
	// const consultModalEmSculpt = new Modal(document.getElementById('consultModalEmSculpt'), {
	// 	keyboard: false,
	// 	backdrop: 'static',
	// });
	// const consultModalCoolSculpt = new Modal(document.getElementById('consultModalCoolSculpt'), {
	// 	keyboard: false,
	// 	backdrop: 'static',
	// });
	const safetyModalLaser = new Modal(document.getElementById('safetyModalLaser'), {
		keyboard: false,
		backdrop: 'static',
	});
	// const safetyModalSkin = new Modal(document.getElementById('safetyModalSkin'), {
	// 	keyboard: false,
	// 	backdrop: 'static',
	// });
	const safetyModalEmSculpt = new Modal(document.getElementById('safetyModalEmSculpt'), {
		keyboard: false,
		backdrop: 'static',
	});
	const safetyModalCoolSculpt = new Modal(document.getElementById('safetyModalCoolSculpt'), {
		keyboard: false,
		backdrop: 'static',
	});
	const addonsModal = new Modal(document.getElementById('addonsModal'), {
		keyboard: false,
		backdrop: 'static',
	});
	const changeTabsModal = new Modal(document.getElementById('changeTabsModal'), {
		keyboard: false,
		backdrop: 'static',
	});
	const productInfoConsultModal = new Modal(document.getElementById('productInfoConsultModal'), {
		keyboard: false,
		backdrop: 'static',
	});

	const safetyModal = function () {
		// console.log('safetyModal', selectedCategorySlug);

		if (selectedCategorySlug === 'laser-hair-removal') {
			return safetyModalLaser;
		} else if (selectedCategorySlug === 'skin-treatments') {
			return null;
		} else if (selectedCategorySlug === 'body-sculpting') {
			if (productIds.includes(CONSULT_EM_SCULPT)) {
				return safetyModalEmSculpt;
			} else if (productIds.includes(CONSULT_COOL_SCULPT)) {
				return safetyModalCoolSculpt;
			} else {
				return null;
			}
		} else {
			return null;
		}
	};

	const consultModal = function () {
		// console.log('safetyModal', selectedCategorySlug);

		if (selectedCategorySlug === 'laser-hair-removal') {
			return consultModalLaser;
		} else if (selectedCategorySlug === 'skin-treatments') {
			return consultModalSkin;
		}
		// else if (selectedCategorySlug === 'body-sculpting') {
		//     if (productIds.includes(CONSULT_EM_SCULPT)) {
		//         return consultModalEmSculpt;
		//     } else if (productIds.includes(CONSULT_COOL_SCULPT)) {
		//         return consultModalCoolSculpt;
		//     } else {
		//         return null;
		//     }
		// }
		else {
			return null;
		}
	};

	const treatmentsProceed = function () {
		const addonsIsEmpty = document.querySelectorAll(`#addons-modal-category-${selectedCategorySlug} .treatment-item`).length === 0;
		const consultIsChosen = productIds.filter((id) => id < 0).length > 0;

		if (consultIsChosen) {
			consultSafety();
			return;
		} else if (addonsIsEmpty) {
			if (!consultChecked) {
				dataLayer.push({
					bookingLocation: selectedBookingLocation ?? 'Unknown',
					event: 'pageview',
					page_location: '/booking-online/consultation-first-time/',
					page_title: 'Booking online - 6.1 - Consultation First time',
				});
				consultModal()?.show();
			} else {
				loadScheduleVisitPage();
			}
		} else {
			dataLayer.push({
				bookingLocation: selectedBookingLocation ?? 'Unknown',
				event: 'pageview',
				page_location: '/booking-online/choose-add-ons',
				page_title: 'Booking online - 5 - Choose Add-ons',
			});
			addonsModal.show();
		}
	};

	const addonsProceed = function () {
		addonsModal.hide();

		// Consult modal
		if (!consultChecked) {
			dataLayer.push({
				bookingLocation: selectedBookingLocation ?? 'Unknown',
				event: 'pageview',
				page_location: '/booking-online/consultation-first-time/',
				page_title: 'Booking online - 6.1 - Consultation First time',
			});
			consultModal()?.show();
		} else {
			loadScheduleVisitPage();
		}
	};

	const consultProceed = function () {
		consultChecked = true;
		includeConsult = false;
		consultModal()?.hide();

		loadScheduleVisitPage();
	};

	const consultSafety = function () {
		consultModal()?.hide();

		dataLayer.push({
			bookingLocation: selectedBookingLocation ?? 'Unknown',
			event: 'pageview',
			page_location: '/booking-online/booking-safety-modal/',
			page_title: 'Booking online - 6.2 - Safety check modal',
		});

		const mySafetyModal = safetyModal();
		if (mySafetyModal) {
			mySafetyModal.show();
		} else {
			// there are no safety questions for this category, proceed.
			safetyProceed();
		}
	};

	const safetyProceed = function () {
		consultChecked = true;
		// only include consult if it's not already selected
		includeConsult = productIds.filter((id) => id < 0).length == 0;
		console.log('includeConsult', includeConsult);
		safetyModal()?.hide();

		loadScheduleVisitPage();
	};

	const types = ['Laser', 'Skin', 'EmSculpt', 'CoolSculpt'];
	for (const key in types) {
		const type = types[key];
		document.getElementById('safetyModal' + type + 'ButtonOk')?.addEventListener('click', safetyProceed);

		document.getElementById('safetyModal' + type + 'ButtonClose')?.addEventListener('click', () => {
			safetyModal()?.hide();
		});

		document.getElementById('safetyModal' + type + 'ButtonClose2')?.addEventListener('click', () => {
			safetyModal()?.hide();
		});

		document.getElementById('safetyModal' + type + 'ButtonBack')?.addEventListener('click', () => {
			safetyModal()?.hide();
			consultModal()?.show();
		});

		document.querySelectorAll('#safetyForm' + type + ' input').forEach((input) => {
			input.addEventListener('change', (event) => {
				safetyFormUpdate(type);
			});
		});

		document.getElementById('consultModal' + type + 'ButtonProceed')?.addEventListener('click', consultProceed);
		document.getElementById('consultModal' + type + 'ButtonSafety')?.addEventListener('click', consultSafety);
		document.getElementById('consultModal' + type + 'ButtonClose')?.addEventListener('click', () => consultModal()?.hide());
	}

	document.getElementById('addonsModalClose')?.addEventListener('click', () => {
		addonsModal.hide();
	});
	// Modal buttons
	document.getElementById('changeTabsModalOk')?.addEventListener('click', () => {
		productIds = [];
		updateAddonsModalCategories();
		refreshChooseTreatmentButtons();
		changeTabsModal.hide();
		selectedCategorySlug = (document.querySelector('#chooseTreatmentTabs .treatment-category.active') as HTMLElement)?.dataset.categorySlug ?? '';
		// console.log('3', selectedCategorySlug);
	});
	document.getElementById('changeTabsModalCancel')?.addEventListener('click', () => {
		const triggerEl = document.querySelector(`#chooseTreatmentTabs .treatment-category[data-category-slug="${selectedCategorySlug}"]`);

		(Tab.getInstance(triggerEl) ?? new Tab(triggerEl)).show();

		changeTabsModal.hide();
	});
	document.getElementById('changeTabsModalCancel2')?.addEventListener('click', () => {
		const triggerEl = document.querySelector(`#chooseTreatmentTabs .treatment-category[data-category-slug="${selectedCategorySlug}"]`);

		(Tab.getInstance(triggerEl) ?? new Tab(triggerEl)).show();

		changeTabsModal.hide();
	});

	// Addons modal
	document.querySelector('.treatments-proceed')?.addEventListener('click', treatmentsProceed);

	document.querySelector('.addons-proceed')?.addEventListener('click', addonsProceed);

	// Bind to the add service buttons
	document.querySelectorAll<HTMLElement>('.add-service').forEach((buttonElement) =>
		buttonElement.addEventListener('click', () => {
			const productIdData = buttonElement.dataset.productId;

			if (productIdData === undefined) {
				return;
			}

			const productId = parseInt(productIdData);

			const enable = !productIds.includes(productId);

			if (enable) {
				// Nuke the productIds list of negative product ids, because all negative numbers are mutually exclusive
				if (productId < 0) {
					productIds = [];
				} else {
					productIds = productIds.filter((id) => id > 0);
				}
				productIds.push(productId);
			} else {
				productIds = productIds.filter((item) => item !== productId);
			}

			refreshChooseTreatmentButtons();
		})
	);

	refreshChooseTreatmentButtons();

	const consults = [CONSULT_COOL_SCULPT, CONSULT_EM_SCULPT, CONSULT_LASER_HAIR_REMOVAL, CONSULT_LASER_SKIN_TREATMENTS];
	document.querySelectorAll('.view-product').forEach((button) => {
		const buttonElement = button as HTMLElement;
		const productIdData = buttonElement.dataset.pid;

		if (productIdData === undefined) {
			return;
		}

		const productId = parseInt(productIdData);

		if (consults.includes(productId)) {
			button.classList.remove('view-product');
			button.classList.add('view-product-consult');

			button.addEventListener('click', (e) => {
				productInfoConsultModal.show();
				e.preventDefault();
			});
		}
	});

	// Tab change intercept
	document.querySelectorAll('#chooseTreatmentTabs .treatment-category').forEach((tab) =>
		tab.addEventListener('click', (e) => {
			if (selectedCategorySlug === (e.target as HTMLElement).dataset.categorySlug) {
				// console.log('1', selectedCategorySlug, (e.target as HTMLElement).dataset.categorySlug);
				return;
			}

			if (productIds.length === 0) {
				selectedCategorySlug = (e.target as HTMLElement).dataset.categorySlug ?? '';
				updateAddonsModalCategories();
				// console.log('2', selectedCategorySlug);
				return;
			}

			changeTabsModal.show();
		})
	);

	rebindAllLocationSelectors();
	onClinicChange();

	const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
	const tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
		return new Tooltip(tooltipTriggerEl);
	});
}
/**
 * Update addons modal categories
 */
function updateAddonsModalCategories() {
	(document.querySelectorAll('.addons-modal-category') as NodeListOf<HTMLElement>).forEach((element) => {
		element.style.display = element.id === 'addons-modal-category-' + selectedCategorySlug ? 'block' : 'none';
	});
}

function safetyFormKeys(type: string) {
	switch (type) {
		case 'Laser':
			// case "Skin":
			return { roaccutane: 'no', pregnant: 'no', pacemaker: 'no' };
		case 'EmSculpt':
			return { 'over-18': 'yes', pregnant: 'no', pacemaker: 'no' };
		case 'CoolSculpt':
			return { 'over-18': 'yes' };
		default:
			return {};
	}
}

/**
 * When the safety modal form is updated, update the buttons
 */
function safetyFormUpdate(type: string) {
	// console.log('safetyFormUpdate', type);

	const safetyForm = document.getElementById('safetyForm' + type) as HTMLFormElement;
	const formData = new FormData(safetyForm);

	let allow = true;
	let disallow = false;

	let keys = safetyFormKeys(type);

	for (const [key, value] of Object.entries(keys)) {
		const allowThis = formData.get(key) === value;
		const disallowThis = !allowThis && formData.get(key) !== null;

		allow = allow && allowThis;
		disallow = disallow || disallowThis;
	}

	if (allow) {
		(document.getElementById('safetyModal' + type + 'ButtonOk') as HTMLInputElement).disabled = false;
		(document.getElementById('safetyModal' + type + 'Ok') as HTMLElement).style.display = 'flex';
		(document.getElementById('safetyModal' + type + 'Fail') as HTMLElement).style.display = 'none';
		return;
	}

	if (disallow) {
		(document.getElementById('safetyModal' + type + 'ButtonOk') as HTMLInputElement).disabled = true;
		(document.getElementById('safetyModal' + type + 'ButtonOk') as HTMLInputElement).disabled = true;
		(document.getElementById('safetyModal' + type + 'Ok') as HTMLElement).style.display = 'none';
		(document.getElementById('safetyModal' + type + 'Fail') as HTMLElement).style.display = 'flex';
		return;
	}

	(document.getElementById('safetyModal' + type + 'ButtonOk') as HTMLInputElement).disabled = true;
	(document.getElementById('safetyModal' + type + 'Ok') as HTMLElement).style.display = 'flex';
	(document.getElementById('safetyModal' + type + 'Fail') as HTMLElement).style.display = 'none';
}

function refreshChooseTreatmentButtons() {
	document.querySelectorAll('.add-service').forEach((button) => {
		const buttonElement = button as HTMLElement;
		const productIdData = buttonElement.dataset.productId;

		if (productIdData === undefined) {
			return;
		}

		const productId = parseInt(productIdData);

		if (productIds.includes(productId)) {
			button.classList.toggle('btn-gold', false);
			button.classList.toggle('btn-red', true);
			buttonElement.innerHTML = '<i class="fas fa-check-circle me-1"></i>ADDED';
			return;
		}

		button.classList.toggle('btn-gold', true);
		button.classList.toggle('btn-red', false);
		buttonElement.innerHTML = 'ADD';
	});

	(document.querySelector('.treatments-proceed') as HTMLButtonElement).disabled = productIds.length === 0;
}

function initializeHorizontalScroller() {
	// Horizontal drag scroll
	// console.log('this thing is not loading');
	const dragScrollHorizontalElements = document.querySelectorAll<HTMLElement>('#timeSlotScroller .drag-scroll-horizontal');
	dragScrollHorizontalElements.forEach((dragScrollHorizontalElement) => {
		// Position
		let pos = {
			left: 0,
			x: 0,
		};

		/**
		 * On mouse move
		 */
		const dragScrollMoveHandler = (e) => {
			const dx = (e.clientX ?? e.touches[0].clientX) - pos.x;
			dragScrollHorizontalElement.scrollLeft = pos.left - dx;
		};

		/**
		 * On mouse down
		 */
		const dragScrollDownHandler = (e) => {
			dragScrollHorizontalElement.style.cursor = 'grabbing';
			dragScrollHorizontalElement.style.userSelect = 'none';

			pos = {
				left: dragScrollHorizontalElement.scrollLeft,
				x: e.clientX ?? e.touches[0].clientX,
			};

			document.addEventListener('mousemove', dragScrollMoveHandler);
			// document.addEventListener('touchmove', dragScrollMoveHandler);
			document.addEventListener('mouseup', dragScrollUpHandler);
			// document.addEventListener('touchend', dragScrollUpHandler);
		};

		/**
		 * On mouse up
		 */
		const dragScrollUpHandler = (e) => {
			document.removeEventListener('mousemove', dragScrollMoveHandler);
			// document.removeEventListener('touchmove', dragScrollMoveHandler);
			document.removeEventListener('mouseup', dragScrollUpHandler);
			// document.removeEventListener('touchend', dragScrollUpHandler);

			// dragScrollHorizontalElement.style.cursor = 'grab';
			// dragScrollHorizontalElement.style.removeProperty('user-select');
		};

		dragScrollHorizontalElement.addEventListener('mousedown', dragScrollDownHandler);
		// dragScrollHorizontalElement.addEventListener('touchsta/rt', dragScrollDownHandler);
	});
}

/**
 * Schedule visit
 */
const BOOKING_SLOT_BUTTON_SELECTOR = '.booking-slot-button';

let simpleSalonCompanyId: any = null;
let selectedBookingLocation: any = null;

let startYearEarliest: number = null;
let startWeekEarliest: number = null;

let initialStartWeek: number = null;
let startYearChoose: number = null;
let startWeekChoose: number = null;

let initialisedEarliestTab = false;
let initialisedChooseTab = false;

let selectedBookingTime: any = null;
let selectedBookingDuration: any = null;
let selectedBookingDisplayDate: any = null;
let selectedBookingDisplayTime: any = null;
let selectedBookingOperatorIds: any = null;
let selectedBookingServiceIds: any = null;

/**
 * Load the schedule visit page
 */
async function loadScheduleVisitPage() {
	currentPage = PAGE_CHOOSE_BOOKING;
	// TODO Clinic selector
	showSpinner();

	dataLayer.push({
		bookingLocation: selectedBookingLocation ?? 'Unknown',
		event: 'pageview',
		page_location: '/booking-online/schedule-visit/',
		page_title: 'Booking online - 7 - Schedule Visit',
	});

	const response = await callWp('GET', 'view_schedule_visit', {
		categorySlug: selectedCategorySlug,
		selectedServiceIds: productIds,
		includeConsult,
		simpleSalonCompanyId,
	});

	rootElement.innerHTML = response.html as string;

	loadScheduleChoose();

	// On tab change, init that section, esp if clinic changed

	document.getElementById('schedule-earliest-tab')?.addEventListener('click', () => {
		if (initialisedEarliestTab) {
			return;
		}
		loadScheduleEarliest();
	});

	document.getElementById('schedule-choose-tab')?.addEventListener('click', () => {
		if (initialisedChooseTab) {
			return;
		}
		loadScheduleChoose();
	});

	document.getElementById('scheduleVisitButtonPrevious')?.addEventListener('click', () => {
		loadChooseTreatmentPage();
	});
	document.getElementById('scheduleVisitButtonNext')?.addEventListener('click', () => {});

	rebindAllLocationSelectors();
	// onClinicChange();

	const confirmBookingModalElement = document.getElementById('confirmBookingModal');
	const confirmBookingModal =
		confirmBookingModalElement === null
			? null
			: new Modal(confirmBookingModalElement, {
					keyboard: false,
					backdrop: 'static',
			  });

	const loadingBookingModalElement = document.getElementById('loadingBookingModal');
	const loadingBookingModal =
		loadingBookingModalElement === null
			? null
			: new Modal(loadingBookingModalElement, {
					keyboard: false,
					backdrop: 'static',
			  });

	const errorBookingModalElement = document.getElementById('errorBookingModal');
	const errorBookingModal =
		errorBookingModalElement === null
			? null
			: new Modal(errorBookingModalElement, {
					keyboard: false,
					backdrop: 'static',
			  });

	document.getElementById('scheduleVisitButtonNext')?.addEventListener('click', () => {
		if (selectedBookingTime === null) {
			return;
		}

		// Show confirmation modal
		confirmBookingModal.show();

		dataLayer.push({
			bookingLocation: selectedBookingLocation ?? 'Unknown',
			event: 'pageview',
			page_location: '/booking-online/confirm-appointment/',
			page_title: 'Booking online - 8 - Confirm Appointment',
		});
	});

	document.getElementById('confirmBookingModalCancel')?.addEventListener('click', () => {
		confirmBookingModal.hide();
	});
	document.getElementById('errorBookingModalCancel')?.addEventListener('click', () => {
		errorBookingModal.hide();
		loadScheduleVisitPage();
	});
	document.getElementById('confirmBookingModalBook')?.addEventListener('click', () => {
		bookSlot();
	});

	document.getElementById('loadMoreAvailableButton')?.addEventListener('click', () => {
		startWeekEarliest++;
		loadScheduleEarliest();
	});

	const bookSlot = async () => {
		confirmBookingModal.hide();
		loadingBookingModal.show();

		console.log({
			simpleSalonCompanyId,
			serviceIds: selectedBookingServiceIds,
			duration: selectedBookingDuration,
			operatorIds: selectedBookingOperatorIds,
			timeStart: selectedBookingTime,
		});

		const bookingResponse = await callWp('POST', 'book_slot', {
			simpleSalonCompanyId,
			serviceIds: selectedBookingServiceIds,
			duration: selectedBookingDuration,
			operatorIds: selectedBookingOperatorIds,
			timeStart: selectedBookingTime,
		});

		loadingBookingModal.hide();
		document.querySelectorAll('.modal-backdrop').forEach((modalBackdrop) => modalBackdrop.remove());

		if (bookingResponse.success !== true) {
			dataLayer.push({
				bookingLocation: selectedBookingLocation ?? 'Unknown',
				event: 'pageview',
				page_location: '/booking-online/time-slot-unavailable/',
				page_title: 'Booking online - 7.2 - Time slot unavailable',
			});
			errorBookingModal.show();
			return;
		}

		const confirmationResponse = bookingResponse.response;
		console.log(confirmationResponse);
		loadBookingConfirmationPage(confirmationResponse);
	};

	const scheduleVisitMessage = document.getElementById('scheduleVisitMessage');
	const scheduleVisitTabs = document.getElementById('scheduleVisitTabs');
	if (scheduleVisitMessage !== null && scheduleVisitTabs !== null) {
		scheduleVisitMessage.style.display = simpleSalonCompanyId === null ? 'flex' : 'none';
		scheduleVisitTabs.style.display = simpleSalonCompanyId === null ? 'none' : 'flex';
	}

	const chooseSlotElement = document.getElementById('choose-slot');
	if (chooseSlotElement !== null) {
		chooseSlotElement.style.display = simpleSalonCompanyId === null ? 'none' : 'flex';
	}

	document.getElementById('header-button-1')?.addEventListener('click', () => {
		loadChooseTreatmentPage();
	});
}

async function onClinicChange() {
	if (currentPage === PAGE_CHOOSE_BOOKING) {
		loadScheduleVisitPage();
		return;
	}

	initialisedEarliestTab = false;
	initialisedChooseTab = false;

	selectedBookingTime = null;
	selectedBookingDuration = null;
	selectedBookingDisplayDate = null;
	selectedBookingDisplayTime = null;
	selectedBookingOperatorIds = null;
	selectedBookingServiceIds = null;

	// load the currently selected tab
	// TODO
	if (document.getElementById('schedule-earliest-tab')?.classList?.contains('active')) {
		if (initialisedEarliestTab) {
			return;
		}
		loadScheduleEarliest();
	}

	if (document.getElementById('schedule-choose-tab')?.classList?.contains('active')) {
		if (initialisedChooseTab) {
			return;
		}
		loadScheduleChoose();
	}

	const scheduleVisitMessage = document.getElementById('scheduleVisitMessage');
	const scheduleVisitTabs = document.getElementById('scheduleVisitTabs');
	if (scheduleVisitMessage !== null && scheduleVisitTabs !== null) {
		scheduleVisitMessage.style.display = simpleSalonCompanyId === null ? 'flex' : 'none';
		scheduleVisitTabs.style.display = simpleSalonCompanyId === null ? 'none' : 'flex';
	}

	const chooseSlotElement = document.getElementById('choose-slot');
	if (chooseSlotElement !== null) {
		chooseSlotElement.style.display = simpleSalonCompanyId === null ? 'none' : 'flex';
	}
}

async function loadScheduleEarliest() {
	initialisedEarliestTab = true;

	const content = document.getElementById('earliestSlotsContainer');
	const loadMoreButton = document.getElementById('loadMoreAvailableButton');
	loadMoreButton.style.display = 'none';

	if (content === null) {
		return;
	}

	const newContent = document.createElement('div');

	content.appendChild(newContent);
	newContent.innerHTML = getSpinnerHTML();

	content.scrollTo({ top: content.scrollHeight });

	const startTime = new Date().getTime();
	const response = await callWp('GET', 'view_earliest_available', {
		categorySlug: selectedCategorySlug,
		selectedServiceIds: productIds,
		includeConsult,
		simpleSalonCompanyId,
		startYear: startYearEarliest ?? null,
		startWeek: startWeekEarliest ?? null,
	});
	const endTime = new Date().getTime();
	const timeToLoad = endTime - startTime;

	newContent.remove();
	content.innerHTML += response.html as string;

	loadMoreButton.style.display = 'block';

	startYearEarliest = parseInt(response.startYear);
	startWeekEarliest = parseInt(response.startWeek);

	rebindBookingButtons(content, timeToLoad);
	refreshBookingButtons();
}

async function loadScheduleChoose() {
	initialisedChooseTab = true;

	const content = document.getElementById('schedule-choose-content');

	if (content === null) {
		return;
	}

	content.innerHTML = getSpinnerHTML();

	const startTime = new Date().getTime();
	const response = await callWp('GET', 'view_choose_day', {
		categorySlug: selectedCategorySlug,
		selectedServiceIds: productIds,
		includeConsult,
		simpleSalonCompanyId,
		startYear: startYearChoose ?? null,
		startWeek: startWeekChoose ?? null,
	});
	const endTime = new Date().getTime();
	const timeToLoad = endTime - startTime;

	startYearChoose = parseInt(response.startYear);
	startWeekChoose = parseInt(response.startWeek);

	if (initialStartWeek === null) {
		initialStartWeek = startWeekChoose;
	}

	content.innerHTML = response.html as string;

	const prevWeekButton = <HTMLButtonElement>document.querySelector('.prev-week-button');
	const nextWeekButton = <HTMLButtonElement>document.querySelector('.next-week-button');

	if (prevWeekButton === null || nextWeekButton === null) {
		return;
	}

	nextWeekButton.addEventListener('click', () => {
		startWeekChoose++;
		loadScheduleChoose();
	});

	prevWeekButton.addEventListener('click', () => {
		startWeekChoose--;
		loadScheduleChoose();
	});

	console.log(startWeekChoose);
	console.log(initialStartWeek);
	console.log(startWeekChoose === initialStartWeek);
	initializeHorizontalScroller();

	prevWeekButton.disabled = startWeekChoose === initialStartWeek;

	rebindBookingButtons(content, timeToLoad);
	refreshBookingButtons();
}

/**
 * Rebind the click listeners
 *
 * @param  element
 */
function rebindBookingButtons(element: any, timeToLoad: any) {
	const bookingButtons = element.querySelectorAll(BOOKING_SLOT_BUTTON_SELECTOR) as NodeListOf<HTMLButtonElement>;

	const numberOfTimeslots = bookingButtons.length;

	const numberOfTimeslotsAm = Array.prototype.slice.call(bookingButtons).filter((b) => b.textContent.includes('AM')).length; // TODO
	const numberOfTimeslotsPm = Array.prototype.slice.call(bookingButtons).filter((b) => b.textContent.includes('PM')).length; // TODO

	dataLayer.push({
		bookingLocation: selectedBookingLocation ?? 'Unknown',
		event: 'timeslots',
		timeslots: numberOfTimeslots,
		timeslotsAm: numberOfTimeslotsAm,
		timeslotsPm: numberOfTimeslotsPm,
		timeToLoad: Math.round((timeToLoad / 1000) * 100) / 100,
	});

	dataLayer.push({
		bookingLocation: selectedBookingLocation ?? 'Unknown',
		event: 'pageview',
		page_location: '/booking-online/schedule-visit-timeslots/',
		page_title: 'Booking online - 7.1 - Schedule Visit - Load Timeslots',
	});

	bookingButtons.forEach((button) => {
		button.addEventListener('click', () => {
			// On a booking click, set that as the selected booking, deselect all others
			const bookingTime = button.dataset.bookingTime ?? null;
			const bookingDuration = button.dataset.bookingDuration ?? null;
			const bookingDisplayDate = button.dataset.bookingDisplayDate ?? null;
			const bookingDisplayTime = button.dataset.bookingDisplayTime ?? null;
			const bookingOperatorIds = JSON.parse(button.dataset.bookingOperatorIds ?? '[]');
			const bookingServiceIds = JSON.parse(button.dataset.bookingServiceIds ?? '[]');

			// Selected booking time
			selectedBookingTime = bookingTime;
			selectedBookingDuration = bookingDuration;
			selectedBookingDisplayDate = bookingDisplayDate;
			selectedBookingDisplayTime = bookingDisplayTime;
			selectedBookingOperatorIds = bookingOperatorIds;
			selectedBookingServiceIds = bookingServiceIds;
			refreshBookingButtons();
		});
	});
}

/**
 * Refresh the booking buttons
 */
function refreshBookingButtons() {
	const bookingButtons = document.querySelectorAll(BOOKING_SLOT_BUTTON_SELECTOR) as NodeListOf<HTMLButtonElement>;
	bookingButtons.forEach((button) => {
		const bookingTime = button.dataset.bookingTime ?? 0;
		const bookingDuration = button.dataset.bookingDuration ?? 0;

		const selected = bookingTime === selectedBookingTime;

		button.classList.toggle('btn-red', selected);

		button.classList.toggle('btn-default', !selected);
		button.classList.toggle('btn-outline', !selected);

		(button.querySelector('.selected-check') as HTMLElement).style.display = selected ? 'inline-block' : 'none';
	});

	const bookingSelected = selectedBookingTime !== null;

	const proceedButton = document.getElementById('scheduleVisitButtonNext') as HTMLButtonElement;
	proceedButton.disabled = !bookingSelected;

	const unselectedBookingContainers = document.querySelectorAll('.unselected-booking-container') as NodeListOf<HTMLElement>;
	unselectedBookingContainers.forEach((container) => {
		container.style.display = bookingSelected ? 'none' : 'flex';
	});

	const selectedBookingContainers = document.querySelectorAll('.selected-booking-container') as NodeListOf<HTMLElement>;
	selectedBookingContainers.forEach((container) => {
		container.style.display = bookingSelected ? 'flex' : 'none';

		if (!bookingSelected) {
			return;
		}

		container.querySelectorAll('.booking-display-time').forEach((element) => {
			element.innerHTML = selectedBookingDisplayTime;
		});
		container.querySelectorAll('.booking-display-date').forEach((element) => {
			element.innerHTML = selectedBookingDisplayDate;
		});
		container.querySelectorAll('.booking-display-location').forEach((element) => {
			element.innerHTML = selectedBookingLocation;
		});
	});
}

/**
 * Location selector
 */
const LOCATION_SELECTOR_SELECTOR = '.location-selector';
const LOCATION_SELECTOR_STATE_SELECTOR = '.location-selector-state';
const LOCATION_SELECTOR_INPUT_SELECTOR = '.location-selector-input';
const LOCATION_SELECTOR_OPTIONS_SELECTOR = '.location-selector-options';
const LOCATION_SELECTOR_OPTION_SELECTOR = '.location-selector-option';
const LOCATION_SELECTOR_EMPTY_MESSAGE = '.location-selector-option-empty';

let locationSelect: any = null;

/**
 * Rebind all location selectors
 */
function rebindAllLocationSelectors() {
	document.querySelectorAll(LOCATION_SELECTOR_SELECTOR).forEach((locationSelector) => rebindLocationSelector(locationSelector as HTMLElement));
}

/**
 * Rebind a locaiton selector
 *
 * @param  locationSelector
 */
function rebindLocationSelector(locationSelector: HTMLElement) {
	// Input
	const locationSelectorInput = locationSelector.querySelector(LOCATION_SELECTOR_INPUT_SELECTOR) as HTMLInputElement;
	if (locationSelectorInput === null) {
		return;
	}

	// Options
	const locationSelectorOptions = locationSelector.querySelector(LOCATION_SELECTOR_OPTIONS_SELECTOR) as HTMLElement;
	if (locationSelectorOptions === null) {
		return;
	}

	// Close if clicked outside of
	window.addEventListener('click', (e) => {
		if (e.composedPath().indexOf(locationSelector) === -1) {
			locationSelectorOptions.style.display = 'none';
			locationSelect?.update();
		}
	});

	// Event listeners
	locationSelectorInput.addEventListener('focus', (e) => locationSelectorFocus(locationSelector, locationSelectorOptions, locationSelectorInput, e));
	locationSelectorInput.addEventListener('blur', (e) => locationSelectorBlur(locationSelector, locationSelectorOptions, locationSelectorInput, e));
	locationSelectorInput.addEventListener('input', (e) => locationSelectorChange(locationSelector, locationSelectorOptions, locationSelectorInput, e));
	locationSelectorInput.addEventListener('change', (e) => locationSelectorChange(locationSelector, locationSelectorOptions, locationSelectorInput, e));

	locationSelector.querySelectorAll(LOCATION_SELECTOR_OPTION_SELECTOR).forEach((locationOptionAny) => {
		const locationOption = locationOptionAny as HTMLElement;
		locationOption.addEventListener('click', () => {
			selectLocationOption(locationSelectorOptions, locationOption);
		});
	});

	// Try to select the location from the location woocommerce id
	if (simpleSalonCompanyId === null) {
		const wooLocationId = getCookie('location_id');
		const defaultLocationElement = <HTMLElement>document.querySelector(LOCATION_SELECTOR_OPTION_SELECTOR + `[data-wp-id="${wooLocationId}"]`);
		if (defaultLocationElement !== null) {
			selectLocationOption(locationSelectorOptions, defaultLocationElement);
		}
	} else {
		const defaultLocationElement = <HTMLElement>document.querySelector(LOCATION_SELECTOR_OPTION_SELECTOR + `[data-company-id="${simpleSalonCompanyId}"]`);
		if (defaultLocationElement !== null) {
			selectLocationOption(locationSelectorOptions, defaultLocationElement);
		}
	}
}

function selectLocationOption(locationSelectorOptions: HTMLElement, locationOption: HTMLElement) {
	const name = locationOption.dataset.name ?? '';
	const id = parseInt(locationOption.dataset.companyId ?? '0');

	document.querySelectorAll(LOCATION_SELECTOR_INPUT_SELECTOR).forEach((input) => {
		(input as HTMLInputElement).value = name;
		(input as HTMLInputElement).placeholder = name;
	});

	locationSelectorOptions.style.display = 'none';
	locationSelect?.update();

	if (simpleSalonCompanyId === id) {
		return;
	}

	selectedBookingLocation = name;
	simpleSalonCompanyId = id;

	onClinicChange();

	// To sync up with main clinic selector
	const wpId = parseInt(locationOption.dataset.wpId ?? '0');
	setCookie('location_id', wpId, 30);
	setCookie('location', name, 30);
	setCookie('zenotiEnabled', locationOption.dataset.zenotiEnabled, 30);

	if (locationOption.dataset.zenotiEnabled === '1') {
		window.location.href = silkApiWp.newBookingSystemUrl;
	}
}

/**
 * On a location selector focus
 *
 * @param  locationSelector
 * @param  locationSelectorOptions
 * @param  locationSelectorInput
 * @param  e
 */
function locationSelectorFocus(locationSelector: HTMLElement, locationSelectorOptions: HTMLElement, locationSelectorInput: HTMLInputElement, e: Event) {
	// Store what the last location was
	// Clear the input, set last location as placeholder
	locationSelectorInput.placeholder = locationSelectorInput.value;
	locationSelectorInput.value = '';

	// Filter reset
	locationSelectorFilter(locationSelectorOptions, '', true);

	// Popper
	locationSelectorOptions.style.display = 'block';
	if (locationSelect === null) {
		locationSelect = Popper.createPopper(locationSelectorInput, locationSelectorOptions, {
			placement: 'bottom',
			// modifiers: [
			// 	{
			// 		name: 'offset',
			// 		options: {
			// 			offset: [0, 17],
			// 		},
			// 	},
			// ],
		});
	}
	locationSelect?.update();
}

/**
 * On loss of focus on a location selector
 *
 * @param  locationSelector
 * @param  locationSelectorOptions
 * @param  locationSelectorInput
 * @param  e
 */
function locationSelectorBlur(locationSelector: HTMLElement, locationSelectorOptions: HTMLElement, locationSelectorInput: HTMLInputElement, e: Event) {
	// Set the input value to the placeholder (last / next location)
	locationSelectorInput.value = locationSelectorInput.placeholder;
	locationSelectorInput.placeholder = '';

	// Hide the selector element if it is open

	// setTimeout(() => {
	// locationSelectorOptions.style.display = 'none';
	// }, 1);
}

/**
 * ON search input change
 *
 * @param  locationSelector
 * @param  locationSelectorOptions
 * @param  locationSelectorInput
 * @param  e
 */
function locationSelectorChange(locationSelector: HTMLElement, locationSelectorOptions: HTMLElement, locationSelectorInput: HTMLInputElement, e: any) {
	// Get the value
	const searchValue = e.target.value;

	// Filter the search
	locationSelectorFilter(locationSelectorOptions, searchValue);
}

/**
 * Filter the location select
 *
 * @param  locationSelectorOptions
 * @param  searchValue
 * @param  reset
 */
function locationSelectorFilter(locationSelectorOptions: HTMLElement, searchValue: any = '', reset: boolean = false) {
	// For each location
	// Visible =
	// If the location contains the term in the name or address
	// If none visible, show none found message
	let empty = false;
	locationSelectorOptions.querySelectorAll(LOCATION_SELECTOR_STATE_SELECTOR).forEach((stateAny) => {
		const state = stateAny as HTMLElement;
		let showState = false;
		state.querySelectorAll(LOCATION_SELECTOR_OPTION_SELECTOR).forEach((locationOptionAny) => {
			const locationOption = locationOptionAny as HTMLElement;
			const show =
				locationOption.dataset.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
				locationOption.dataset.address?.toLowerCase().includes(searchValue.toLowerCase()) ||
				reset;
			locationOption.style.display = show ? 'flex' : 'none';

			showState = showState || show;
		});

		empty = empty || showState;

		state.style.display = showState ? 'block' : 'none';
	});

	(locationSelectorOptions.querySelector(LOCATION_SELECTOR_EMPTY_MESSAGE) as HTMLElement).style.display = !empty ? 'block' : 'none';
}

/**
 * Load the schedule visit page
 *
 * TODO store this in session / cookie for if the page reloads?
 *
 * @param  confirmationResponse
 */
async function loadBookingConfirmationPage(confirmationResponse: any) {
	currentPage = PAGE_BOOKING_CONFIRMED;

	showSpinner();

	dataLayer.push({
		bookingLocation: selectedBookingLocation ?? 'Unknown',
		event: 'pageview',
		page_location: '/booking-online/booking-confirmed/',
		page_title: 'Booking online - 9 - Booking Confirmed',
	});

	const response = await callWp('GET', 'view_booking_confirmation', { response: JSON.stringify(confirmationResponse) });

	rootElement.innerHTML = response.html as string;

	document.querySelectorAll('.modal-backdrop').forEach((modalBackdrop) => modalBackdrop.remove());

	// GTM tracking event for successful booking
	if (confirmationResponse.data_layer_event) {
		for (const event of confirmationResponse.data_layer_event) {
			dataLayer.push(event);
		}
		console.log('dataLayerEvent', confirmationResponse.data_layer_event);
	}
}

export {};
